import { default as React } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  AlertApi,
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';

import { WorkloadClient } from '@internal/workload-api-helper';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAsync } from 'react-use';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import {
  Account,
  AccountDetailsCardProps,
  Annotations,
  DepartmentEntry,
  FormFieldName,
  OrgUnitEntry,
} from '../types';
import { Departments } from './Departments';
import { useAllowedOrgUnit } from '../hooks/useAllowedOrgUnit';
import { useDataGovernanceTagsEnabled } from '../hooks/useDataGovernanceTags';
import { BaseEditIcon } from '../BaseEditIcon';
import { EditAccountTagsIconDialog } from './EditAccountTagsIconDialog';

let accountId: string;

const isValueDefined = (value?: any) => value !== undefined && value !== null;

const isOkayResponse = (response?: Response) => response?.ok === true;

const objectIsEmpty = (object: Object) => Object.keys(object).length === 0;

const isResponseCodeAcceptable = (responseCode?: number) =>
  responseCode !== undefined && responseCode > 100 && responseCode < 205;

export const updatePayload = ({
  pfg_owner,
  pfg_cost_center,
  pfg_app_inventory_id,
  pfg_alert_group,
  pfg_data_asset_owner,
  department,
  pfg_org_unit,
  business_unit,
  isPfgOrgUnitAvailable,
  dataGovernanceTagsEnabled,
}: {
  pfg_owner?: string | (string | undefined)[];
  pfg_cost_center?: string | (string | undefined)[];
  pfg_app_inventory_id?: string | (string | undefined)[];
  pfg_alert_group?: string | (string | undefined)[];
  pfg_data_asset_owner?: string | (string | undefined)[];
  department?: string | (string | undefined)[];
  pfg_org_unit?: string | (string | undefined)[];
  business_unit?: string | (string | undefined)[];
  isPfgOrgUnitAvailable?: boolean;
  dataGovernanceTagsEnabled?: boolean;
}): { [key: string]: string | (string | undefined)[] } => {
  const payload: { [key: string]: string | (string | undefined)[] } = {};

  if (pfg_owner) {
    payload['pfg-owner'] = pfg_owner;
  }
  if (pfg_cost_center) {
    payload['pfg-cost-center'] = pfg_cost_center;
  }
  if (pfg_app_inventory_id) {
    payload['pfg-app-inventory-id'] = pfg_app_inventory_id;
  }
  if (pfg_alert_group) {
    payload['pfg-alert-group'] = pfg_alert_group;
  }
  // A value can survive in form state after the flag is turned off, so the flag
  // has to be checked here as well as where the input is rendered — otherwise
  // a save would still write the tag.
  if (dataGovernanceTagsEnabled && pfg_data_asset_owner) {
    payload['pfg-data-asset-owner'] = pfg_data_asset_owner;
  }
  if (department) {
    payload.department = department;
  }
  if (isPfgOrgUnitAvailable && pfg_org_unit && business_unit) {
    payload['pfg-org-unit'] = `principal.${business_unit}.${pfg_org_unit}`;
  }

  return payload;
};

const patchResourceData = async ({
  workload,
  workloadClient,
  resourceName,
  payload,
}: {
  workloadClient: WorkloadClient;
  workload?: string;
  resourceName?: string;
  payload: { [key: string]: string | (string | undefined)[] };
}) => {
  return await workloadClient.apiFetch(
    `workloads/workload/${workload}/resource/${resourceName}`,
    'PATCH',
    payload,
  );
};

const parseWorkloadObjectValues = (
  catalogLocationStringList: string[] | undefined,
): { [key: string]: string | undefined } => {
  const resourceFileName: string | undefined = catalogLocationStringList?.pop();
  const resourceName = resourceFileName?.split('.')[0];
  const workload = catalogLocationStringList?.pop();

  return {
    resourceName,
    workload,
  };
};

const getFilteredDepartments = (entity: any) => {
  const department_list_raw = Departments;
  const department_list_filtered: DepartmentEntry[] = [];
  department_list_raw.forEach(dept => {
    if (
      dept.bu_abbr ===
      entity?.metadata?.labels?.['business-unit-abbr'].toUpperCase()
    ) {
      department_list_filtered.push(dept);
    }
  });
  return department_list_filtered;
};

const getDepartmentDefault = (entity: any) => {
  const department_list_filtered = getFilteredDepartments(entity);
  const current_department = entity?.metadata?.labels?.department || 'no-entry';
  const empty_department: DepartmentEntry = { id: '', title: '', bu_abbr: '' };
  let department_default = department_list_filtered.find(
    dept => dept.id === current_department,
  );
  if (department_default === undefined) {
    department_default = empty_department;
  }
  return department_default.id;
};

const getFilteredOrgUnits = (entity: any, allowedOrgUnits: OrgUnitEntry[]) => {
  const org_unit_list_raw: OrgUnitEntry[] = allowedOrgUnits;
  const org_unit_list_filtered: OrgUnitEntry[] = [];
  org_unit_list_raw.forEach(org_unit => {
    if (
      org_unit.bu_abbr ===
      entity?.metadata?.labels?.['business-unit-abbr'].toUpperCase()
    ) {
      org_unit_list_filtered.push(org_unit);
    }
  });
  return org_unit_list_filtered;
};

const getOrgUnitDefault = (entity: any, allowedOrgUnits: OrgUnitEntry[]) => {
  const org_unit_list_filtered = getFilteredOrgUnits(entity, allowedOrgUnits);
  const current_org_unit =
    entity?.spec?.['pfg-org-unit']?.split('.')[2] || 'no-entry';
  const empty_org_unit: OrgUnitEntry = { id: '', title: '', bu_abbr: '' };
  let org_unit_default = org_unit_list_filtered.find(
    org_unit => org_unit.id === current_org_unit,
  );
  if (org_unit_default === undefined) {
    org_unit_default = empty_org_unit;
  }
  return org_unit_default.id;
};

const updateResource = async ({
  workloadClient,
  workloadObject,
  payload,
  stopLoadingAndCloseModal,
  alertApi,
  killProcessAndReportError,
}: {
  workloadClient: WorkloadClient;
  workloadObject: any;
  payload: { [key: string]: string | (string | undefined)[] };
  stopLoadingAndCloseModal: Function;
  alertApi: AlertApi;
  killProcessAndReportError: Function;
}) => {
  try {
    const patchResourceDataResponse = await patchResourceData({
      workloadClient,
      workload: workloadObject.workload,
      resourceName: workloadObject.resourceName,
      payload,
    });
    if (isOkayResponse(patchResourceDataResponse) === false) {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'update your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve();
    }
  } catch (e: any) {
    killProcessAndReportError(e);
  }
  return Promise.resolve();
};

export function EditAccountTagsIcon(props: AccountDetailsCardProps) {
  const { allowedOrgUnits } = useAllowedOrgUnit();
  const dataGovernanceTagsEnabled = useDataGovernanceTagsEnabled();
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const identity = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const [workloadData, setWorkloadData] = useState<Account>();
  const [workloadDataAPIResponse, setWorkloadDataAPIResponse] =
    useState<Response>();
  const [preChangeTagFormValues, setPreChangeTagFormValues] = React.useState(
    {},
  );
  const [isLoadingWorkloadData, setIsLoadingWorkloadData] =
    useState<boolean>(true);

  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(config, oauthApi);

  const { entity } = useEntity();
  const annotations = entity?.metadata?.annotations as Annotations;
  accountId = annotations?.['amazonaws.com/account-id'];

  const isPfgOrgUnitAvailable = entity?.spec?.['pfg-org-unit'] ? true : false;

  const department_list_filtered = React.useMemo((): (
    | DepartmentEntry
    | OrgUnitEntry
  )[] => {
    if (isPfgOrgUnitAvailable) {
      return allowedOrgUnits
        ? getFilteredOrgUnits(entity, allowedOrgUnits)
        : [];
    }
    return getFilteredDepartments(entity);
  }, [isPfgOrgUnitAvailable, allowedOrgUnits, entity]);

  useAsync(async (): Promise<Account> => {
    setIsLoadingWorkloadData(true);
    const workloadDetails = await workloadClient.fetchWorkloadData(accountId);

    setWorkloadDataAPIResponse(workloadDetails.response);

    const details = (await workloadDetails.json()) as Account;
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);
    setIsLoadingWorkloadData(false);

    return details;
  }, []);

  const FormFieldEnum: FormFieldName = Object.freeze({
    pfg_cost_center: 'pfg_cost_center',
    pfg_app_inventory_id: 'pfg_app_inventory_id',
    pfg_alert_group: 'pfg_alert_group',
    pfg_data_asset_owner: 'pfg_data_asset_owner',
    pfg_owner: 'pfg_owner',
    department: 'department',
    pfg_org_unit: 'pfg_org_unit',
  });

  // Data Asset Owner is a required field, so leaving it in this list while its
  // input is hidden would fail validation and block every save.
  const formFields = Object.values(FormFieldEnum).filter(
    field =>
      dataGovernanceTagsEnabled || field !== FormFieldEnum.pfg_data_asset_owner,
  );

  const formContext = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      [FormFieldEnum.pfg_cost_center!]:
        entity?.metadata?.labels?.['pfg-cost-center'],
      [FormFieldEnum.pfg_app_inventory_id!]: String(
        entity?.metadata?.labels?.['pfg-app-inventory-id'],
      ),
      [FormFieldEnum.pfg_alert_group!]: String(
        entity?.metadata?.labels?.['pfg-alert-group'] || 'no-entry',
      ),
      [FormFieldEnum.pfg_data_asset_owner!]: String(
        entity?.spec?.['pfg-data-asset-owner'] || '',
      ),
      [FormFieldEnum.pfg_owner!]: String(entity?.spec?.['pfg-owner'] || ''),
      [FormFieldEnum.department!]: String(getDepartmentDefault(entity)),
      ['workload_environments']: [entity?.metadata?.labels?.['pfg-env-type']],
      [FormFieldEnum.pfg_org_unit!]: String(
        getOrgUnitDefault(entity, allowedOrgUnits),
      ),
    },
  });

  const apiValidationPayload = {
    form: formContext,
    identity: identity,
    config: config,
  };

  const { variant } = props;

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const handleClickOpen = () => {
    formContext.reset({
      [FormFieldEnum.pfg_cost_center!]:
        entity?.metadata?.labels?.['pfg-cost-center'],
      [FormFieldEnum.pfg_app_inventory_id!]: String(
        entity?.metadata?.labels?.['pfg-app-inventory-id'],
      ),
      [FormFieldEnum.pfg_alert_group!]: String(
        entity?.metadata?.labels?.['pfg-alert-group'] || 'no-entry',
      ),
      [FormFieldEnum.pfg_data_asset_owner!]: String(
        entity?.spec?.['pfg-data-asset-owner'] || '',
      ),
      [FormFieldEnum.pfg_owner!]: String(entity?.spec?.['pfg-owner'] || ''),
      [FormFieldEnum.department!]: String(getDepartmentDefault(entity)),
      ['workload_environments']: [entity?.metadata?.labels?.['pfg-env-type']],
      [FormFieldEnum.pfg_org_unit!]: String(
        getOrgUnitDefault(entity, allowedOrgUnits),
      ),
    });

    const preSubmitFormValues: {
      [key: string]: string | (string | undefined)[] | undefined;
    } = apiValidationPayload.form.getValues();

    const matchingPreSubmitFormValues = Object.fromEntries(
      Object.entries(preSubmitFormValues).map(([key, value]) => [
        key.replaceAll('_', '-'),
        value,
      ]),
    );

    setPreChangeTagFormValues(matchingPreSubmitFormValues);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Reset form to initial values from entity to clear any unsaved changes
    formContext.reset({
      [FormFieldEnum.pfg_cost_center!]:
        entity?.metadata?.labels?.['pfg-cost-center'],
      [FormFieldEnum.pfg_app_inventory_id!]: String(
        entity?.metadata?.labels?.['pfg-app-inventory-id'],
      ),
      [FormFieldEnum.pfg_alert_group!]: String(
        entity?.metadata?.labels?.['pfg-alert-group'] || 'no-entry',
      ),
      [FormFieldEnum.pfg_data_asset_owner!]: String(
        entity?.spec?.['pfg-data-asset-owner'] || '',
      ),
      [FormFieldEnum.pfg_owner!]: String(entity?.spec?.['pfg-owner'] || ''),
      [FormFieldEnum.department!]: String(getDepartmentDefault(entity)),
      ['workload_environments']: [entity?.metadata?.labels?.['pfg-env-type']],
      [FormFieldEnum.pfg_org_unit!]: String(
        getOrgUnitDefault(entity, allowedOrgUnits),
      ),
    });
  };

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
    setIsLoading(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const isFormValid = async () => {
    let response = false;

    await Promise.all(
      formFields.map(fieldName => formContext.trigger(fieldName)),
    );

    if (Object.keys(formContext.formState.errors).length === 0) {
      response = true;
    }

    return response;
  };

  const handleConfirm = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    if ((await isFormValid()) === false) {
      return Promise.resolve();
    }

    const formValues: {
      [key: string]: string | (string | undefined)[] | undefined;
    } = apiValidationPayload.form.getValues();

    const payload: { [key: string]: string | (string | undefined)[] } =
      updatePayload({
        pfg_owner: formValues[FormFieldEnum.pfg_owner!],
        pfg_cost_center: formValues[FormFieldEnum.pfg_cost_center!],
        pfg_app_inventory_id: formValues[FormFieldEnum.pfg_app_inventory_id!],
        pfg_alert_group: formValues[FormFieldEnum.pfg_alert_group!],
        pfg_data_asset_owner: formValues[FormFieldEnum.pfg_data_asset_owner!],
        dataGovernanceTagsEnabled,
        department: isPfgOrgUnitAvailable
          ? formValues[FormFieldEnum.pfg_org_unit!]
          : formValues[FormFieldEnum.department!],
        pfg_org_unit: formValues[FormFieldEnum.pfg_org_unit!],
        business_unit: entity?.metadata?.labels?.['business-unit'],
        isPfgOrgUnitAvailable: isPfgOrgUnitAvailable,
      });

    if (isEqual(payload, preChangeTagFormValues)) {
      setIsLoading(false);
      setIsModalOpen(false);
      return Promise.resolve();
    }

    if (objectIsEmpty(payload)) {
      stopLoadingAndCloseModal();
      return Promise.resolve();
    }

    let workloadObject: { [key: string]: string | undefined } = {};
    let workloadDataStatus: number | undefined;

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');

    if (workloadDataAPIResponse?.ok && catalogLocationStringList) {
      workloadDataStatus = workloadDataAPIResponse?.status;
      workloadObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve();
    }

    if (
      isResponseCodeAcceptable(workloadDataStatus) &&
      isValueDefined(workloadObject?.workload)
    ) {
      try {
        await updateResource({
          workloadClient,
          workloadObject,
          payload,
          stopLoadingAndCloseModal,
          alertApi,
          killProcessAndReportError,
        });

        stopLoadingAndCloseModal();

        alertApi.post({
          message: getMessage({
            successMessage: 'Updating account information.',
          }).SUCCESS,
          severity: SeverityEnum.INFO,
        });

        catalogApi.refreshEntity(stringifyEntityRef(entity));
      } catch (err: any) {
        alertApi.post({
          message: getMessage({ failureToDo: 'updating account .yaml file.' })
            .FAILURE,
          severity: SeverityEnum.ERROR,
        });
      }
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({ failureToDo: 'retrieve workload' }).FAILURE,
        severity: SeverityEnum.WARNING,
      });
    }
    return Promise.resolve();
  };

  return (
    <BaseEditIcon
      workloadData={workloadData}
      requesterEmail={requesterEmail}
      handleClickOpen={handleClickOpen}
      isModalOpen={isModalOpen}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      disableConfirm={
        isLoading ||
        Object.keys(formContext.formState.errors).length > 0 ||
        !formContext.formState.isDirty
      }
      apiValidationPayload={apiValidationPayload}
      isLoading={isLoading}
      isLoadingEdit={isLoadingWorkloadData}
      dialogTitle="Update Account Tags"
      settingName="Enterprise Tags"
      allowOwnerEdit
      allowEngineerEdit
    >
      <EditAccountTagsIconDialog
        variant={variant}
        formContext={formContext}
        apiValidationPayload={apiValidationPayload}
        isLoading={isLoading}
        department_list_filtered={department_list_filtered}
        isPfgOrgUnitAvailable={isPfgOrgUnitAvailable}
      />
    </BaseEditIcon>
  );
}
